import React, { useEffect } from "react";
import moment from "moment";
import {
	CustomField,
	CustomFieldType
} from "@/Contracts/Settings";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import { MergeTagVariableDataType } from "@/Contracts/Documents";
import ZEditorMergeTag from "@molecules/ZMergeTag/ZEditorMergetag";
import { IRenderCustomFieldFormItemProps } from "./interface";
import { EWidgets } from "../ZForm/interface";
import FormBuilder from "antd-form-builder";
import "./ZRenderCustomFieldFormItem.less";
import useDatePattern from "@/Hooks/useDatePattern";
import { CurrencyOptions, validUrlRegex } from "@/Contracts";

const ZRenderCustomFieldFormItem: React.FC<
	IRenderCustomFieldFormItemProps
> = props => {
	const { customField, form, values, formAlignment, isLoading } = props;

	const datePattern = useDatePattern();

	const isReadonly =
		props.isReadonly || customField.is_value_editable_from_ui === false;

	const getMergeTagDataType = (field: CustomFieldType) => {
		switch (field) {
			case CustomFieldType.TextField:
				return MergeTagVariableDataType.TEXT;
			case CustomFieldType.NumberField:
				return MergeTagVariableDataType.NUMBER;
			case CustomFieldType.DecimalField:
				return MergeTagVariableDataType.DOUBLE;
			case CustomFieldType.AmountField:
				return MergeTagVariableDataType.DOUBLE;
			default:
				return undefined;
		}
	};

	const data_type_merge_tag = getMergeTagDataType(customField.field_type);

	const parentValueChanged = (_value: string) => {
		const childCustomField = props.customFieldList?.find(
			field => field.parent_field_info?.parent_id === customField.id
		);

		if (childCustomField) {
			const formFieldName = `custom_field_form_values.${childCustomField.id}`;
			form.setFieldsValue({
				[formFieldName]: undefined
			});
		}
	};

	const getCustomFieldFormItem = (currentCustomField: CustomField) => {
		switch (currentCustomField.field_type) {
			case CustomFieldType.TextField: {
				return {
					widget: EWidgets.INPUT,
					widgetProps: {
						"data-testid": "text-custom-field",
						placeholder: `Please enter ${currentCustomField.display_name}`,
						width: "full"
					},
					rules: [
						{
							whitespace:
								currentCustomField.mandatory_field_details
									?.for_ui ?? false,
							message: `Please enter ${currentCustomField.display_name}`
						}
					]
				};
			}
			case CustomFieldType.UrlField: {
				return {
					widget: EWidgets.INPUT,
					widgetProps: {
						placeholder: `Please enter ${currentCustomField.display_name}`,
						width: "full"
					},
					rules: [
						{
							message: `Please enter ${currentCustomField.display_name}`,
							pattern: validUrlRegex
						}
					]
				};
			}
			case CustomFieldType.DateField: {
				return {
					widget: EWidgets.DATEPICKER,
					widgetProps: {
						datePattern,
						"data-testid": "date-custom-field",
						placeholder: `Please enter ${currentCustomField.display_name}`,
						className: "w-full"
					}
				};
			}
			case CustomFieldType.NumberField: {
				const inputOnChange = (
					event: React.KeyboardEvent<HTMLInputElement>
				) => {
					if (event.key === "." || event.key === "e") {
						event.preventDefault();
					}
				};
				return {
					widget: EWidgets.INPUTNUMBER,
					widgetProps: {
						min: 0,
						step: 1,
						"data-testid": "number-custom-field",
						placeholder: `Please enter ${currentCustomField.display_name}`,
						width: "full",
						onKeyDown: inputOnChange
					}
				};
			}
			case CustomFieldType.DecimalField:
			case CustomFieldType.AmountField: {
				const inputOnChange = (
					event: React.KeyboardEvent<HTMLInputElement>
				) => {
					if (event.key === "e") {
						event.preventDefault();
					}
				};
				return {
					widget: EWidgets.INPUTNUMBER,
					widgetProps: {
						min: 0,
						step: 0.01,
						"data-testid": "decimal-custom-field",
						placeholder: `Please enter ${currentCustomField.display_name}`,
						width: "full",
						onKeyDown: inputOnChange
					}
				};
			}
			case CustomFieldType.Dropdown:
			case CustomFieldType.MultiSelect: {
				const parentCustomFieldId =
					currentCustomField.parent_field_info?.parent_id;

				if (!!parentCustomFieldId) {
					const parentCustomField = props.customFieldList?.find(
						field => field.id === parentCustomFieldId
					);

					if (!parentCustomField) {
						throw new Error(
							`No parent custom field for ${currentCustomField.display_name} found !!`
						);
					}

					const parentCustomFieldValue =
						form.getFieldValue("custom_field_form_values") &&
						form.getFieldValue("custom_field_form_values")[
							parentCustomField.id
						];

					const allowedValues =
						currentCustomField.parent_field_info?.allowed_values?.[
							parentCustomFieldValue
						];

					return {
						widget: EWidgets.SELECT,
						widgetProps: {
							"data-testid": `child.${customField.id}`,
							placeholder: `Please select ${currentCustomField.display_name}`,
							selectOptions: allowedValues?.map(
								(allowedDropdownValue, index) => {
									return {
										key: index,
										value: allowedDropdownValue,
										name: allowedDropdownValue
									};
								}
							),
							mode:
								currentCustomField.field_type ===
								CustomFieldType.MultiSelect
									? "multiple"
									: undefined
						}
					};
				}

				return {
					widget: EWidgets.SELECT,
					widgetProps: {
						"data-testid": `dropdown.${customField.id}`,
						placeholder: `Please select ${currentCustomField.display_name}`,
						onChange: parentValueChanged,
						selectOptions:
							currentCustomField.allowed_dropdown_values?.map(
								(allowedDropdownValue, index) => {
									return {
										key: index,
										value: allowedDropdownValue,
										name: allowedDropdownValue
									};
								}
							),
						mode:
							currentCustomField.field_type ===
							CustomFieldType.MultiSelect
								? "multiple"
								: undefined
					}
				};
			}
			case CustomFieldType.DropdownFieldWithKeyValue: {
				return {
					widget: EWidgets.SELECT,
					widgetProps: {
						disabled: isReadonly,
						"data-testid": `child.${customField.id}`,
						placeholder: `Please select ${currentCustomField.display_name}`,
						selectOptions:
							customField.allowed_dropdown_key_values?.map(
								dropdownOption => ({
									name: dropdownOption.value,
									value: dropdownOption.key
								})
							)
					}
				};
			}
			case CustomFieldType.CurrencyField: {
				return {
					widget: EWidgets.SELECT,
					widgetProps: {
						disabled: isReadonly,
						"data-testid": `child.${customField.id}`,
						placeholder: `Please select ${currentCustomField.display_name}`,
						selectOptions: CurrencyOptions.map(a => ({
							name: a.label,
							value: a.symbol
						}))
					}
				};
			}
			case CustomFieldType.FormulaField: {
				return {
					widget: EWidgets.INPUT,
					widgetProps: {
						"data-testid": "text-custom-field",
						disabled: true,
						width: "full"
					}
				};
			}
			default: {
				return {};
			}
		}
	};

	const isDateCustomField =
		customField.field_type === CustomFieldType.DateField;

	const initialValue = values
		? isDateCustomField && values[0]
			? moment(values[0])
			: customField.field_type === CustomFieldType.MultiSelect
			? values
			: values[0]
		: undefined;

	useEffect(() => {
		if (props.initialValues) {
			props.initialValues.forEach(value => {
				props.form.setFieldsValue({
					[`custom_field_form_values.${value.id}`]: isDateCustomField
						? moment(value.value).toString() !== "Invalid date"
							? moment(value.value)
							: undefined
						: value.value
				});
			});
		}
	}, [props.initialValues]);

	return (
		<FormBuilder
			form={form}
			disabled={isReadonly || isLoading}
			meta={{
				formItemLayout: formAlignment ?? [5, 19],
				fields: [
					{
						key: `custom_field_form_values.${customField.id}`,
						className: "z-render-custom-fields",
						label: `${capitalizeFirstLetter(
							customField.display_name
						)}`,
						hasFeedback: true,
						required:
							customField.mandatory_field_details?.for_ui ??
							false,
						formItemProps: {
							shouldUpdate: (prevValue: any, nextValue: any) =>
								!!customField.parent_field_info?.parent_id &&
								prevValue.custom_field_form_values?.[
									customField.parent_field_info?.parent_id
								] !==
									nextValue.custom_field_form_values?.[
										customField.parent_field_info?.parent_id
									]
						},
						message: `Please enter ${customField.display_name}`,
						initialValue: initialValue,
						...(!(props.mergeTagUsed && data_type_merge_tag)
							? getCustomFieldFormItem(customField)
							: {
									widget: ZEditorMergeTag,
									widgetProps: {
										id: `${props.from}-${customField.id}`,
										MergeTags: props?.mergeTags ?? [],
										placeholde: `Please enter ${customField.display_name}`,
										border: true,
										data_type: data_type_merge_tag
									}
							  })
					}
				]
			}}
		/>
	);
};

export default ZRenderCustomFieldFormItem;
